import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AuthService } from '../../services/auth.service';
import { CloudBackupService } from '../../services/cloud-backup.service';
import { StorageService } from '../../services/storage.service';

@Component({
  selector: 'app-settings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatDividerModule,
    FormsModule,
  ],
  template: `
    <h2 mat-dialog-title>User Settings</h2>
    <mat-dialog-content>
      <div class="settings-section">
        <h3>Budgeting & Cloud Backup</h3>
        <p class="section-description">
          Enable secure, encrypted cloud backups for your budgeting data across your devices.
        </p>
        <mat-slide-toggle
          [(ngModel)]="isCloudBackupEnabled"
          (change)="onCloudBackupToggleChange($event.checked)"
        >
          Enable Secure Cloud Backup
        </mat-slide-toggle>
      </div>

      <mat-divider></mat-divider>

      <div class="settings-section">
        <h3>Diagnostics & Error Reporting</h3>
        <p class="section-description">
          Help us improve Suite Life Tools by automatically reporting unhandled application errors
          and bug traces during testing.
        </p>
        <mat-slide-toggle
          [(ngModel)]="isErrorReportingEnabled"
          (change)="onErrorReportingToggleChange($event.checked)"
        >
          Enable Automatic Error Reporting
        </mat-slide-toggle>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .settings-section {
        padding: 1rem 0;
      }
      .section-description {
        font-size: 0.85rem;
        color: #666;
        margin-bottom: 1rem;
      }
    `,
  ],
})
export class SettingsDialogComponent {
  private dialogRef = inject(MatDialogRef<SettingsDialogComponent>);
  private storage = inject(StorageService);
  private auth = inject(AuthService);
  private cloudBackup = inject(CloudBackupService);

  // Initial opt-in states from localStorage
  isCloudBackupEnabled: boolean = localStorage.getItem('cloud_backup_opt_in') === 'true';
  isErrorReportingEnabled: boolean = localStorage.getItem('error_reporting_opt_in') === 'true';

  async onCloudBackupToggleChange(enabled: boolean) {
    localStorage.setItem('cloud_backup_opt_in', enabled ? 'true' : 'false');

    const user = this.auth.currentUser();
    if (user?.id) {
      if (enabled) {
        await this.storage.forceImmediateBackup(user.id);
      } else {
        this.cloudBackup.deleteBackup(user.id).subscribe({
          next: () => console.log('Cloud backup deleted successfully upon opt-out.'),
          error: (err) => console.error('Failed to delete cloud backup:', err),
        });
      }
    }
  }

  onErrorReportingToggleChange(enabled: boolean) {
    localStorage.setItem('error_reporting_opt_in', enabled ? 'true' : 'false');
  }
}
